import { type ComponentType, type FC, type ReactNode } from "react";

import { Button, Colors } from "@snapchat/snap-design-system";
import { AddIcon, type IconProps, PencilIcon } from "@snapchat/snap-design-system-icons";

import { Flexbox, LabelWithTooltip } from "../../../shared/components";
import { type ShowModal } from "../../../shared/hooks";

import { SidebarPanelStyle, SidebarPanelText, TextContainer, sectionLabel } from "./LensSidebar.styles";

interface SidebarPanelProps {
    label: string | ReactNode;
    tooltipContent: ReactNode;
    description: ReactNode;
    disabled: boolean;
    Icon: ComponentType<IconProps>;
    readMoreElement?: ReactNode;
    showModal: ShowModal;
    noContentAction?: "add" | "manage" | "custom";
    noContentCustom?: ReactNode;
    actionLabel?: string;
    children?: ReactNode;
    dataTestIdPrefix?: string;
}

export const SidebarPanel: FC<SidebarPanelProps> = ({
    label,
    tooltipContent,
    description,
    Icon,
    readMoreElement,
    children,
    showModal,
    disabled,
    noContentAction = "add",
    noContentCustom,
    actionLabel,
    dataTestIdPrefix = "",
}) => (
    <div className={SidebarPanelStyle}>
        <LabelWithTooltip
            dataTestId={`sb-${dataTestIdPrefix}-heading`}
            className={sectionLabel}
            label={label}
            content={tooltipContent}
        />
        {children ? (
            <>
                {children}
                <Button
                    data-testid={`sb-${dataTestIdPrefix}-button`}
                    size="small"
                    onClick={showModal}
                    isDisabled={disabled}
                >
                    <PencilIcon data-testid={`sb-${dataTestIdPrefix}-pencil`} size={12} />
                    <span data-testid={`sb-${dataTestIdPrefix}-button-text`}>
                        {`Edit ${actionLabel ?? (typeof label === "string" ? label : "")}`}
                    </span>
                </Button>
            </>
        ) : (
            <Flexbox alignItems="flex-start">
                <Flexbox style={{ width: 24 }} justifyContent="center">
                    <Icon data-testid={`sb-${dataTestIdPrefix}-icon`} size={24} fill={Colors.Black.V100} />
                </Flexbox>
                <TextContainer>
                    <SidebarPanelText>
                        {description}
                        {readMoreElement ?? null}
                    </SidebarPanelText>
                    {noContentAction === "manage" ? (
                        <Button
                            data-testid={`sb-${dataTestIdPrefix}-button`}
                            size="small"
                            onClick={showModal}
                            isDisabled={disabled}
                        >
                            <PencilIcon data-testid={`sb-${dataTestIdPrefix}-pencil`} size={12} />
                            <span data-testid={`sb-${dataTestIdPrefix}-button-text`}>
                                {`Manage ${actionLabel ?? (typeof label === "string" ? label : "")}`}
                            </span>
                        </Button>
                    ) : noContentAction === "add" ? (
                        <Button
                            data-testid={`sb-${dataTestIdPrefix}-button`}
                            size="small"
                            onClick={showModal}
                            isDisabled={disabled}
                        >
                            <AddIcon data-testid={`sb-${dataTestIdPrefix}-add`} size={12} />
                            <span data-testid={`sb-${dataTestIdPrefix}-button-text`}>
                                {`Add ${actionLabel ?? (typeof label === "string" ? label : "")}`}
                            </span>
                        </Button>
                    ) : (
                        noContentCustom
                    )}
                </TextContainer>
            </Flexbox>
        )}
    </div>
);
